import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { AuthRepository, UserRole } from 'app/core/auth/auth.repository';

export type SupervisorScope = 'mine' | 'all';

const STORAGE_KEY = 'supervisor_student_scope';

/**
 * "My students / All students" filter for supervisors.
 * A supervisor restricted by an admin (only_assigned_students) is always
 * limited server-side, so the filter is hidden for him.
 */
@Injectable({ providedIn: 'root' })
export class SupervisorScopeService {
  private readonly _scope = new BehaviorSubject<SupervisorScope>(
    localStorage.getItem(STORAGE_KEY) === 'mine' ? 'mine' : 'all'
  );
  readonly scope$ = this._scope.asObservable();

  constructor(private authRepository: AuthRepository) {}

  get scope(): SupervisorScope {
    return this._scope.value;
  }

  get isSupervisor(): boolean {
    return Number(this.authRepository.getUser()?.role) === UserRole.Supervisor;
  }

  get isForced(): boolean {
    return Number(this.authRepository.getUser()?.supervisor_only_assigned || 0) === 1;
  }

  /** The toggle is offered only to supervisors who may choose. */
  get canChoose(): boolean {
    return this.isSupervisor && !this.isForced;
  }

  setScope(scope: SupervisorScope): void {
    if (scope === this._scope.value) return;
    localStorage.setItem(STORAGE_KEY, scope);
    this._scope.next(scope);
  }

  /** Adds scope=mine for supervisors who picked "My students". */
  applyTo(params: HttpParams): HttpParams {
    return this.isSupervisor && this.scope === 'mine' ? params.set('scope', 'mine') : params;
  }
}
